import { Launch } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/system";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import GrupoChip from "../../../../components/grupoEconomico/GrupoChip";
import LineChartComponent from "../../../../components/lineComponent";
import api from "../../../../services/apiCliente";
import DatagridComponent from "../../../../components/datagridComponent";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

const INVERSED_POWER_RX =
  window?.config?.REACT_APP_INVERSED_POWER_RX ||
  process.env.REACT_APP_INVERSED_POWER_RX;

// Tooltip customizado
const CustomTooltip = ({
  active,
  payload,
  label,
  valueUnit = "",
  title = "",
  formatValue,
  color = "green",
  theme,
  styles,
}) => {
  if (!active || !payload || payload.length === 0) return null;

  const { clock, value } = payload?.[0]?.payload || {};

  const formattedValue = formatValue ? formatValue(value) : value?.toFixed(2);

  return (
    <Box
      sx={
        styles.boxTooltip || {
          padding: "8px",
          display: "flex",
          flexDirection: "column",
        }
      }
    >
      <Typography fontSize={"14px"}>
        {`Data: ${dayjs?.unix(clock)?.tz(timeZone)?.format("DD/MM/YY HH:mm")}`}
      </Typography>

      <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
        <Box
          sx={{
            ...styles.customTooltip,
            bgcolor: theme.palette.cardGraphic?.graphColor?.[color] || color,
            width: "12px",
            height: "12px",
            borderRadius: "2px",
          }}
        />
        <Typography fontSize={"14px"}>
          {title || "Valor"}: {formattedValue} {valueUnit}
        </Typography>
      </Stack>
    </Box>
  );
};

export default function TabONU({
  tableOnu,
  search,
  theme,
  styles,
  formatUptime,
  navigate,
  initial_date,
  final_date,
  labelErp,
  filteredRows,
  loading,
}) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [receivedPower, setReceivedPower] = useState({
    currentValue: null,
    data: [],
  });
  const [transmittedPower, setTransmittedPower] = useState({
    currentValue: 0,
    data: [],
  });

  const [loadingPower, setLoadingPower] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getOnuParams = (row) => {
    const parts =
      row?.onuItem
        ?.split("-")
        .map((s) => s.trim())
        .filter(Boolean) || [];
    return {
      serialOnu: parts[0] || "",
      usernameCliente: parts[1] || "",
      posicaoOnu: "",
    };
  };

  const getReceivedPower = async (signal, params) => {
    const [receivedPowerHistory, lastReceivedPower] = await Promise.all([
      api.post(
        "/cliente/zabbix/serieTemporal",
        {
          hostids: selectedRow?.hostId,
          device: selectedRow?.device,
          serialOnu: params.serialOnu,
          usernameCliente: params.usernameCliente,
          posicaoOnu: params.posicaoOnu,
          initial_date,
          final_date,
          nameFilter: "receivedPower",
        },
        { signal },
      ),
      api.post(
        "/cliente/zabbix/serieTemporal",
        {
          hostids: selectedRow?.hostId,
          device: selectedRow?.device,
          serialOnu: params.serialOnu,
          usernameCliente: params.usernameCliente,
          posicaoOnu: params.posicaoOnu,
          nameFilter: "receivedPower",
          final_date,
          lastItem: true,
        },
        { signal },
      ),
    ]);

    setReceivedPower({
      currentValue: lastReceivedPower?.data?.value ?? null,
      data: receivedPowerHistory?.data || [],
    });
  };

  const getTransmittedPower = async (signal, params) => {
    const [transmittedPowerHistory, lastTransmittedPower] = await Promise.all([
      api.post(
        "/cliente/zabbix/serieTemporal",
        {
          hostids: selectedRow?.hostId,
          device: selectedRow?.device,
          serialOnu: params.serialOnu,
          usernameCliente: params.usernameCliente,
          posicaoOnu: params.posicaoOnu,
          initial_date,
          final_date,
          nameFilter: "transmittedPower",
        },
        { signal },
      ),
      api.post(
        "/cliente/zabbix/serieTemporal",
        {
          hostids: selectedRow?.hostId,
          device: selectedRow?.device,
          serialOnu: params.serialOnu,
          usernameCliente: params.usernameCliente,
          posicaoOnu: params.posicaoOnu,
          nameFilter: "transmittedPower",
          final_date,
          lastItem: true,
        },
        { signal },
      ),
    ]);

    setTransmittedPower({
      currentValue: lastTransmittedPower?.data?.value || 0,
      data: transmittedPowerHistory?.data || [],
    });
  };

  useEffect(() => {
    if (!selectedRow || Object.keys(selectedRow).length === 0) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchPowerData = async () => {
      setLoadingPower(true);
      try {
        const onuParams = getOnuParams(selectedRow);
        await Promise.all([
          getReceivedPower(signal, onuParams),
          getTransmittedPower(signal, onuParams),
        ]);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Erro ao buscar dados de potência da ONU:", error);
        }
      } finally {
        setLoadingPower(false);
      }
    };

    fetchPowerData();
    return () => controller.abort();
  }, [selectedRow, initial_date, final_date]);

  const handleLinkClick = (e, params) => {
    e.stopPropagation();

    const clientStorage = {
      interfaceId: params?.row?.onuItem,
      hostId: params?.row?.hostId,
      device: params?.row?.device,
      selectTime: 5,
      initialDate: null,
      finalDate: null,
      serviceId: params?.row?.servicoId,
    };

    localStorage.setItem(
      "filterDataClientFromHomeNavigation",
      JSON.stringify(clientStorage),
    );

    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      window.open(`/home/onu`, "_blank");
    } else {
      navigate(`/home/onu`);
    }
  };

  const linkView = (params) => (
    <Box sx={styles.launchContainer}>
      <Tooltip title={"Navegar até a onu"}>
        <Launch
          size="small"
          sx={{ ...styles.launchIcon, cursor: "pointer" }}
          onClick={(e) => handleLinkClick(e, params)}
          onAuxClick={(e) => {
            if (e.button === 1) {
              handleLinkClick(e, params);
            }
          }}
        />
      </Tooltip>
    </Box>
  );

  // Grupo econômico: exibe a empresa membro dona de cada ONU
  const temEmpresaGrupo = tableOnu?.some((row) => !!row?.ownerNome);

  const columnsOnu = [
    { field: "id", headerName: "ID", hide: true },
    {
      field: "onuItem",
      headerName: "ONU",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params?.value || ""}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
              fontSize: "inherit",
            }}
          >
            {params?.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "nomeServico",
      headerName: labelErp,
      flex: 1.5,
    },
    // Coluna exibida apenas para usuários de grupo econômico (IXC)
    ...(temEmpresaGrupo
      ? [
          {
            field: "ownerNome",
            headerName: "Empresa",
            flex: 1,
            renderCell: (params) =>
              params?.row?.ownerNome ? (
                <GrupoChip variant="owner" label={params?.row?.ownerNome} />
              ) : (
                ""
              ),
          },
        ]
      : []),

    {
      field: "potencia",
      headerName: "Potência",
      flex: 0.6,
      valueGetter: (value, row) => {
        const power = row?.receivedPower;
        return power !== null && power !== undefined
          ? `${Number(power).toFixed(2)} dBm`
          : "N/A";
      },
    },
    {
      field: "receivedPower",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => {
        const raw = params?.row?.receivedPower;
        const value = parseFloat(raw);
        const isNA = raw === null || raw === undefined || isNaN(value);

        const getChipColor = (val) => {
          if (isNA) return "neutral";
          if (val <= -27) return "critical";
          if (val <= -25) return "high";
          return "success";
        };
        const getChipLabel = (val) => {
          if (isNA) return "N/A";
          if (val <= -27) return "Crítica";
          if (val <= -25) return "Atenção";
          return "Ok";
        };

        const colorKey = getChipColor(value);
        const isNeutral = colorKey === "neutral";

        return (
          <Chip
            label={getChipLabel(value)}
            sx={{
              backgroundColor: isNeutral
                ? theme.palette.chip.background.default
                : theme.palette.chip.background[colorKey],
              color: isNeutral
                ? theme.palette.text.disabled
                : theme.palette.chip.colorText[colorKey],
              border: "0.6px solid",
              borderColor: isNeutral
                ? theme.palette.text.disabled
                : theme.palette.chip.colorText[colorKey],
              borderRadius: "10px",
              height: "20px",
              width: "70px",
            }}
            size="small"
          />
        );
      },
    },
    {
      field: "link",
      headerName: "",
      sortable: false,
      width: 0.1,
      renderCell: linkView,
    },
  ];

  const formatReceivedData = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => {
      const raw = parseFloat(item?.value);
      if (isNaN(raw)) return { ...item, value: null };
      const val =
        raw <= 0
          ? INVERSED_POWER_RX
            ? -raw
            : raw
          : INVERSED_POWER_RX
            ? raw
            : null;
      return { ...item, value: val };
    });
  };

  const formatTransmittedData = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => {
      const raw = parseFloat(item?.value);
      return { ...item, value: isNaN(raw) ? null : raw };
    });
  };

  const POWER_GRAPHS = [
    {
      title: "Potência Recebida",
      type: "received",
      valueUnit: "dBm",
      colorKey: "blue",
      showChipStatus: true,
      minValue: INVERSED_POWER_RX ? 0 : -40,
      maxValue: INVERSED_POWER_RX ? 40 : 0,
      getChipColor: (value) => {
        if (value === null || value === undefined) return "neutral";
        if (value <= -27) return "critical";
        if (value <= -25) return "high";
        return "success";
      },
      getChipLabel: (value) => {
        if (value === null || value === undefined) return "N/A";
        if (value <= -27) return "Crítica";
        if (value <= -25) return "Atenção";
        return "Ok";
      },
      formatValue: (value) =>
        value != null && value !== 0
          ? `${INVERSED_POWER_RX ? Math.abs(parseFloat(value)).toFixed(2) : parseFloat(value).toFixed(2)}`
          : "0",
      getData: () => formatReceivedData(receivedPower?.data),
      currentValue: receivedPower?.currentValue ?? null,
    },
    {
      title: "Potência Transmitida",
      type: "transmitted",
      valueUnit: "dBm",
      colorKey: "orange",
      showChipStatus: false,
      minValue: 0,
      maxValue: undefined,
      formatValue: (value) =>
        value != null && value !== 0 ? parseFloat(value).toFixed(2) : " - ",
      getData: () => formatTransmittedData(transmittedPower?.data),
      currentValue: transmittedPower?.currentValue || 0,
    },
  ];

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        {loading && <LinearProgress />}
        <Paper
          sx={{ ...styles.paperClientes, height: "500px" }}
          elevation={theme.palette.mode === "dark" ? 0 : 3}
        >
          <DatagridComponent
            columns={columnsOnu}
            rows={filteredRows || []}
            setSelectedRow={setSelectedRow}
            isPagination={false}
            onRowClick={(params) => setSelectedRow(params.row)}
            sxDatagrid={{
              height: "100%",
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundImage: theme.palette.gradient.buttonSelected,
                "& .MuiDataGrid-cell": { color: theme.palette.white.main },
              },
              "& .MuiDataGrid-row:hover": {
                backgroundImage: `linear-gradient(
                                    90deg,
                                    ${alpha(theme.palette.buttonHoverGradient.dark, 0.5)} 0%,
                                    ${alpha(theme.palette.buttonHoverGradient.light, 0.5)} 100%
                                )`,
              },
            }}
            options={{ disableRowSelectionOnClick: false }}
          />
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        {selectedRow ? (
          // Paper Externo (Container principal idêntico ao do CPE com 500px de altura)
          <Paper
            elevation={theme.palette.mode === "dark" ? 0 : 3}
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
              gap: 1,
              height: "500px",
              p: "10px",
              borderRadius: "10px",
            }}
          >
            {POWER_GRAPHS.map((graph) => {
              const chartData = graph.getData();

              const mainColor =
                theme.palette.cardGraphic?.graphColor?.[graph.colorKey] ||
                graph.colorKey;
              const lineColor =
                theme.palette.cardGraphic?.graphLineColor?.[graph.colorKey] ||
                graph.colorKey;

              const resolvedMaxValue =
                graph.maxValue !== undefined
                  ? graph.maxValue
                  : Math.max(...chartData.map((d) => Number(d.value) || 0));

              return (
                // Paper Interno (Outlined flex: 1 para dividir o espaço em dois blocos de ~250px)
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "10px",
                    overflow: "hidden",
                    position: "relative",
                    p: 1,
                  }}
                  key={graph.type}
                >
                  {loadingPower && (
                    <LinearProgress
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1,
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      ...styles.boxHeader,
                      mb: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",

                        fontWeight: "bold",
                      }}
                    >
                      {graph.title}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "12px", fontWeight: "bold" }}
                        >
                          Atual:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: mainColor,
                          }}
                        >
                          {`${graph.formatValue(graph.currentValue)} ${graph.valueUnit}`}
                        </Typography>
                      </Box>
                      {graph.showChipStatus &&
                        (() => {
                          const chipColorKey = graph.getChipColor(
                            graph.currentValue,
                          );
                          const chipNeutral = chipColorKey === "neutral";
                          return (
                            <Chip
                              label={graph.getChipLabel(graph.currentValue)}
                              sx={{
                                backgroundColor: chipNeutral
                                  ? "transparent"
                                  : theme.palette.chip.background[chipColorKey],
                                color: chipNeutral
                                  ? theme.palette.text.disabled
                                  : theme.palette.chip.colorText[chipColorKey],
                                border: "0.6px solid",
                                borderColor: chipNeutral
                                  ? theme.palette.text.disabled
                                  : theme.palette.chip.colorText[chipColorKey],
                                borderRadius: "10px",
                                height: "20px",
                              }}
                              size="small"
                            />
                          );
                        })()}
                    </Box>
                  </Box>

                  {/* Gráfico preenchendo o restante do Paper outlined */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "150px",
                    }}
                  >
                    {loadingPower ? (
                      <CircularProgress size={30} sx={{ color: mainColor }} />
                    ) : (
                      <Box
                        sx={{
                          flex: 1, // Cresce para ocupar o resto do Paper
                          position: "relative",
                          width: "100%",
                          minHeight: "150px", // Garantia de altura
                        }}
                      >
                        {loadingPower ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                            }}
                          >
                            <CircularProgress
                              size={30}
                              sx={{ color: mainColor }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              bottom: 0,
                              left: 0,
                              right: 0,
                            }}
                          >
                            <LineChartComponent
                              data={chartData}
                              mainColor={mainColor}
                              mainLineColor={lineColor}
                              minValuebps={graph.minValue}
                              maxValuebps={resolvedMaxValue}
                              customTooltip={(props) => (
                                <CustomTooltip
                                  {...props}
                                  valueUnit={graph.valueUnit}
                                  title={graph.title}
                                  formatValue={graph.formatValue}
                                  color={graph.colorKey}
                                  theme={theme}
                                  styles={styles}
                                />
                              )}
                              yOptions={{
                                tickFormatter: (value) => {
                                  const n = parseFloat(value);
                                  if (isNaN(n)) return `0 ${graph.valueUnit}`;
                                  return `${n.toFixed(1)} ${graph.valueUnit}`;
                                },
                              }}
                              xOptions={{
                                tickFormatter: (clock) =>
                                  dayjs
                                    .unix(clock)
                                    .tz(timeZone)
                                    .format("DD/MM HH:mm"),
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Paper>
        ) : (
          <Paper
            elevation={theme.palette.mode === "dark" ? 0 : 3}
            sx={{
              ...styles.paperClientes,
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography color="textSecondary">
              {`Selecione um ${labelErp.toLowerCase()} para visualizar os gráficos de potência.`}
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}
